import { useCallback, useLayoutEffect, useRef } from 'react'

/**
 * In-memory, per-key scroll memory for a single scroll container.
 *
 * Designed for modals whose body is ONE shared scroll container but shows
 * different "frames" (e.g. a content stack toggled via display:none). Pass the
 * active frame's key; when it changes, the outgoing frame's scrollTop is already
 * remembered (saved on every scroll) and the incoming frame's saved position is
 * restored synchronously (before paint), so navigating back returns to the exact
 * spot instead of jumping to the top.
 *
 * Positions live in memory for the container's lifetime (reset on unmount) — this
 * is intentionally NOT persisted across reopens.
 *
 * Wire it once on the scroll container:
 *   const { setScrollRef, onScroll } = useScrollMemory(activeKey)
 *   <div ref={setScrollRef} onScroll={onScroll} />
 */
export const useScrollMemory = (activeKey?: string) => {
  const scrollRef = useRef<HTMLElement | null>(null)
  const positions = useRef<Map<string, number>>(new Map())

  const setScrollRef = useCallback((node: HTMLElement | null) => {
    scrollRef.current = node
  }, [])

  const onScroll = useCallback(() => {
    if (activeKey == null || !scrollRef.current) return
    positions.current.set(activeKey, scrollRef.current.scrollTop)
  }, [activeKey])

  useLayoutEffect(() => {
    if (activeKey == null || !scrollRef.current) return
    scrollRef.current.scrollTop = positions.current.get(activeKey) ?? 0
  }, [activeKey])

  return { setScrollRef, onScroll }
}
